import { useMemo } from 'react'
import {
  Vignette,
  EffectComposer,
  Noise,
  ColorDepth,
  HueSaturation,
  ChromaticAberration,
  Pixelation,
  Scanline,
  BrightnessContrast,
} from '@react-three/postprocessing'
import { BlendFunction, Effect } from 'postprocessing'
import { Uniform, Vector2 } from 'three'
import { useControls } from 'leva'
import { extend } from '@react-three/fiber'

import { cfgLente } from '../../config/lente'

/* ============================================
   BARREL DISTORTION EFFECT (custom)
   ============================================ */

// Barrel: strength > 0 = “grandangolo che pancia verso fuori”
// cylindricalRatio ≈ quanto “stiri” sull’asse orizzontale (tipo esempio che hai postato)
const fragmentShader = /* glsl */ `
  uniform float strength;
  uniform float cylindricalRatio;
  uniform vec2 resolution;
  uniform float zoom;   // 👈 nuovo

  void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
    // uv 0..1 → p -1..1
    vec2 p = uv * 2.0 - 1.0;

    // zoom: >1 = zoom in, <1 = zoom out
    p /= zoom;

    float aspect = resolution.x / resolution.y;
    p.x *= aspect * cylindricalRatio;

    float r2 = dot(p, p);
    float k = strength;
    vec2 pd = p * (1.0 + k * r2);
    pd.x /= aspect * cylindricalRatio;

    vec2 sampleUV = pd * 0.5 + 0.5;

    sampleUV = clamp(sampleUV, 0.0, 1.0);
    vec4 col = texture2D(inputBuffer, sampleUV);
    outputColor = col;
  }
`;

// Implementazione Effect per postprocessing
class BarrelDistortionImpl extends Effect {
  constructor({ strength = 0.5, cylindricalRatio = 2.0, zoom = 1.0 } = {}) {
    super('BarrelDistortionEffect', fragmentShader, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map([
        ['strength', new Uniform(strength)],
        ['cylindricalRatio', new Uniform(cylindricalRatio)],
        ['resolution', new Uniform(new Vector2(1, 1))],
        ['zoom', new Uniform(zoom)],
      ]),
    })
  }

  update(renderer) {
    const size = renderer.getSize(new Vector2())
    this.uniforms.get('resolution').value.copy(size)
  }
}

// registra il tipo nella runtime R3F (facoltativo, ma harmless)
extend({ BarrelDistortionImpl })

// wrapper React da usare come <BarrelDistortionEffect ... />
function BarrelDistortionEffect({ strength = 0.5, cylindricalRatio = 2.0, zoom = 1.0 }) {
  const effect = useMemo(
    () => new BarrelDistortionImpl({ strength, cylindricalRatio, zoom }),
    [strength, cylindricalRatio, zoom]
  )
  return <primitive object={effect} />
}

/* ============================================
   EFFECTS COMPONENT
   ============================================ */

export default function Effects() {
  const blendOptions = Object.keys(BlendFunction).reduce((acc, key) => {
    acc[key] = BlendFunction[key];
    return acc;
  }, {});

  const { blendMode, strength, cylindricalRatio, zoom } = useControls({
    blendMode: { value: BlendFunction.NORMAL, options: blendOptions },
    strength: { value: 0.17, min: -1.0, max: 1.0, step: 0.01 },
    cylindricalRatio: { value: 1.25, min: 0.25, max: 4.0, step: 0.05 },
    zoom: { value: 1.44, min: 0.5, max: 2.0, step: 0.01 },
  })

  // I click devono rifare lo stesso conto della lente, altrimenti si
  // mira l'immagine deformata e si colpisce quella non deformata.
  cfgLente.strength = strength
  cfgLente.cylindricalRatio = cylindricalRatio
  cfgLente.zoom = zoom

  // Preset "retro PSX" (stile Buckshot Roulette): low-res pixellato,
  // grana pesante, scanline CRT, colori compressi e desaturati.
  // SPENTO di default: è un esperimento attivabile dal pannello Leva
  // (?debug → Grafica → stileRetro). OFF = grafica classica.
  const retro = useControls('Grafica', {
    stileRetro: false,
    pixel: { value: 4, min: 1, max: 16, step: 1 },            // granularità pixel
    grana: { value: 0.45, min: 0, max: 1, step: 0.01 },       // rumore/pellicola
    scanline: { value: 0.18, min: 0, max: 1, step: 0.01 },    // righe CRT
    bitColore: { value: 13, min: 8, max: 16, step: 1 },       // banding colori
    desaturazione: { value: -0.3, min: -1, max: 0, step: 0.01 },
    contrasto: { value: 0.06, min: 0, max: 0.5, step: 0.01 },
    vignetta: { value: 0.75, min: 0, max: 1, step: 0.01 },
  }, { collapsed: true })

  // --- Look retro PSX/CRT ---
  // NB: niente offset sulla ChromaticAberration e bitColore >= 12:
  // con posterizzazione più aggressiva le ombre collassano in nero
  // pieno e l'aberrazione crea "fantasmi" verdi delle silhouette
  if (retro.stileRetro) {
    return (
      <EffectComposer multisampling={0}>
        <Pixelation granularity={retro.pixel} />
        <ColorDepth bits={retro.bitColore} opacity={1} />
        <Noise blendFunction={blendMode} opacity={retro.grana} premultiply />
        <HueSaturation saturation={retro.desaturazione} opacity={1} />
        <BrightnessContrast brightness={0} contrast={retro.contrasto} />
        <ChromaticAberration />
        <Scanline blendFunction={BlendFunction.OVERLAY} density={1.3} opacity={retro.scanline} />
        <Vignette offset={0.42} darkness={retro.vignetta} eskil={false} />
        <BarrelDistortionEffect strength={strength} cylindricalRatio={cylindricalRatio} zoom={zoom} />
      </EffectComposer>
    )
  }

  // --- Grafica classica (stato precedente, invariato) ---
  return (
    <>
      <EffectComposer multisampling={0}>
        <Vignette offset={0.59} darkness={0.6} eskil={false} />
        <Noise blendFunction={blendMode} opacity={0.4} premultiply />
        <ColorDepth bits={13} opacity={1} />
        <HueSaturation saturation={0.3} opacity={1} />
        <ChromaticAberration />

        <BarrelDistortionEffect strength={strength} cylindricalRatio={cylindricalRatio} zoom={zoom} />
      </EffectComposer>
    </>
  )
}
